import * as THREE from 'three';
import { type ThreeEvent } from '@react-three/fiber';
import { useCallback, useState } from 'react';
import type {
  InstancedMeshIntersection,
  PickResult,
  PointPickResult,
  PointsIntersection,
  ViewerEvent,
} from '../types';
import { type PointCoordinates } from './PointCoordinates';
import { useOnClick } from './useOnClick';
import { useAbyssViewerContext } from './AbyssViewerContext';

export interface UseClickHandlersProps {
  camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
  pickPointCloud: (nativeEvent: PointerEvent | MouseEvent) => PointPickResult | undefined;
  setCenterOfRotation: (target: [x: number, y: number, z: number]) => void;
  isBrushToolActive: () => boolean;
  // user handlers
  onClick?: (viewerEvent: ViewerEvent<PointerEvent>) => void;
  onMeshClick?: (point: PointCoordinates, event: ThreeEvent<MouseEvent>) => void;
  onPointCloudClick?: (pointPickResult: PointPickResult) => void;
  onPointCloudHover?: (pointPickResult: PointPickResult | undefined) => void;
}

export interface GlobalClickHandlers {
  onPointerMoveGlobal: (event: ThreeEvent<PointerEvent>) => void;
  onPointerUpGlobal: (event: ThreeEvent<PointerEvent>) => void;
  onPointerDownGlobal: (event: ThreeEvent<PointerEvent>) => void;
  onPointerOutGlobal: (event: ThreeEvent<PointerEvent>) => void;
}

export const useClickHandlers = ({
  camera,
  pickPointCloud,
  setCenterOfRotation,
  isBrushToolActive,
  onClick,
  onMeshClick,
  onPointCloudClick,
  onPointCloudHover,
}: UseClickHandlersProps): GlobalClickHandlers => {
  const { setHoveredInstance } = useAbyssViewerContext();

  const pickCenterOfRotation = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      const { nativeEvent } = event;
      let pointPosition: THREE.Vector3 | undefined;
      const pickResult = pickPointCloud(nativeEvent);
      if (pickResult) {
        const pointAttributes = pickResult?.getPointAttributes(pickResult.pointIndex, ['position']);
        const position = pointAttributes.get('position');
        if (Array.isArray(position) && position.length === 3) {
          pointPosition = new THREE.Vector3(...position);
        }
      }
      let intersetionPosition: THREE.Vector3 | undefined;
      if (event && event.intersections.length > 0) {
        // first intersection is closest to camera
        intersetionPosition = event.intersections[0].point;
      }

      const getClosestPointToCamera = (
        point0?: THREE.Vector3,
        point1?: THREE.Vector3
      ): THREE.Vector3 | undefined => {
        if (!point0) return point1;
        if (!point1) return point0;
        return camera.position.distanceToSquared(point0) < camera.position.distanceToSquared(point1)
          ? point0
          : point1;
      };

      const target = getClosestPointToCamera(pointPosition, intersetionPosition);

      if (target) {
        const { x, y, z } = target;
        setCenterOfRotation([x, y, z]);
      }
    },
    [pickPointCloud, camera, setCenterOfRotation]
  );

  const onClickDefault = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (event.stopped) return;

      const { nativeEvent } = event;

      // handling left mouse button only
      if (nativeEvent.button !== 0) {
        return;
      }

      if (event.intersections.length === 0) return;

      const intersection = event.intersections[0];
      if (intersection.object instanceof THREE.Points) {
        // it's a point picking result from the HybridRaycaster
        const pointsIntersection: PointsIntersection =
          intersection as unknown as PointsIntersection;

        const { pointPickInfo } = pointsIntersection;

        const pickResult: PointPickResult = {
          ...pointPickInfo,
          mouseEvent: event.nativeEvent,
        };
        // const pickResultOld = pickPointCloud(nativeEvent);
        // if (pickResult.pointIndex !== pickResultOld?.pointIndex) {
        //   console.log('new pickResult', pickResult);
        //   console.log('old pickResult', pickResultOld);
        //   throw new Error("Old and new picking results don't match");
        // }

        onPointCloudClick?.(pickResult);
      } else if (intersection.object instanceof THREE.InstancedMesh) {
        // it's a mesh picking result from the HybridRaycaster
        const meshIntersection: InstancedMeshIntersection =
          intersection as unknown as InstancedMeshIntersection;

        const { pickInfo } = meshIntersection;

        const { onClick } = pickInfo;
        onClick?.(pickInfo.instanceIndex, event);
      } else {
        onMeshClick?.(event.intersections[0].point, event);
      }
    },
    [onMeshClick, onPointCloudClick]
  );

  const callOnClickHandler = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (onClick !== undefined) {
        const viewerEvent: ViewerEvent<PointerEvent> = {
          event,
          nativeEvent: event.nativeEvent,
          pickPointCloud,
        };
        onClick(viewerEvent);
      } else {
        onClickDefault(event);
      }
    },
    [pickPointCloud, onClick, onClickDefault]
  );

  const handleLeftMouseClick = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      // console.log('handleLeftMouseClick', { viewerEvent });
      const { nativeEvent } = event;
      const { ctrlKey, altKey, shiftKey, metaKey } = nativeEvent;
      if (ctrlKey && !altKey && !shiftKey && !metaKey) {
        // Ctrl-click: center of rotation
        pickCenterOfRotation(event);
        event?.stopPropagation();
        return;
      }

      if (isBrushToolActive()) {
        return;
      }

      callOnClickHandler(event);
    },
    [pickCenterOfRotation, isBrushToolActive, callOnClickHandler]
  );

  const handleRightMouseClick = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      // console.log('handleRightMouseClick', { viewerEvent });

      callOnClickHandler(event);
    },
    [callOnClickHandler]
  );

  const handleMouseClick = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      // console.log('handleMouseClick', { viewerEvent });

      const { nativeEvent } = event;

      switch (nativeEvent.button) {
        case 0:
          handleLeftMouseClick(event);
          break;
        case 2:
          handleRightMouseClick(event);
          break;
        default:
      }
    },
    [handleLeftMouseClick, handleRightMouseClick]
  );

  const [lastPointHover, setLastPointHover] = useState<PointPickResult>();

  const onPointerMoveGlobal = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (event.stopped) return;

      // console.log('onPointerMoveGlobal', event);

      if (event.intersections.length === 0) return;

      let pickResult: PickResult | undefined;
      let pointPickResult: PointPickResult | undefined;

      const intersection = event.intersections[0];
      if (intersection.object instanceof THREE.Points) {
        // it's a point picking result from the HybridRaycaster
        const pointsIntersection: PointsIntersection =
          intersection as unknown as PointsIntersection;

        const { pointPickInfo } = pointsIntersection;

        pointPickResult = {
          ...pointPickInfo,
          mouseEvent: event.nativeEvent,
        };
      } else if (intersection.object instanceof THREE.InstancedMesh) {
        // it's a mesh picking result from the HybridRaycaster
        const meshIntersection: InstancedMeshIntersection =
          intersection as unknown as InstancedMeshIntersection;

        const { pickInfo } = meshIntersection;

        pickResult = pickInfo;
      }
      if (lastPointHover !== pointPickResult) {
        setLastPointHover(pointPickResult);
        onPointCloudHover?.(pointPickResult);
      }
      setHoveredInstance?.((current) => {
        if (!pickResult) return undefined;
        const { groupKey, instanceIndex } = pickResult;
        if (current?.groupKey === groupKey && current.instanceIndex === instanceIndex)
          return current;
        return { groupKey, instanceIndex };
      });
    },
    [setHoveredInstance, onPointCloudHover, lastPointHover]
  );

  const onPointerOutGlobal = useCallback(() => {
    setLastPointHover(undefined);
    onPointCloudHover?.(undefined);
    setHoveredInstance?.(undefined);
  }, [onPointCloudHover, setHoveredInstance]);

  const { onPointerUp: onPointerUpGlobal, onPointerDown: onPointerDownGlobal } = useOnClick({
    onClick: handleMouseClick,
  });

  return {
    onPointerMoveGlobal,
    onPointerUpGlobal,
    onPointerDownGlobal,
    onPointerOutGlobal,
  };
};
